import React, { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Plus, ArrowUp, ArrowDown } from "lucide-react";

const resolve = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);

export default function ImageListField({ value = [], onChange, testId = "images", accept = "image/*" }) {
  const [urlInput, setUrlInput] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const add = (v) => {
    if (!v) return;
    onChange([...(value || []), v]);
  };
  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const arr = [...value];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const addUrl = () => {
    const v = urlInput.trim();
    if (!v) return;
    add(v);
    setUrlInput("");
  };
  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      }
      onChange([...(value || []), ...uploaded]);
      toast.success(`Uploaded ${uploaded.length} image${uploaded.length !== 1 ? "s" : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex gap-2">
        <Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="Paste image URL…"
          className="rounded-none flex-1" data-testid={`${testId}-url`} />
        <button type="button" onClick={addUrl} className="btn-gold-outline px-3 py-2 text-[10px] uppercase tracking-widest flex items-center gap-1" data-testid={`${testId}-add-url`}>
          <Plus size={12} /> Add
        </button>
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="btn-primary-ink px-3 py-2 text-[10px] uppercase tracking-widest flex items-center gap-1 disabled:opacity-50" data-testid={`${testId}-upload`}>
          <Upload size={12} /> {busy ? "Uploading…" : "Upload files"}
        </button>
        <input ref={inputRef} type="file" accept={accept} multiple className="hidden" onChange={(e) => upload(Array.from(e.target.files || []))} />
      </div>

      {value.length === 0 && <div className="text-[11px] text-ink-500">No images yet. Add a URL or upload files.</div>}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {value.map((src, i) => (
          <div key={i} className="relative border hairline bg-white p-2 group" data-testid={`${testId}-item-${i}`}>
            <img src={resolve(src)} alt="" className="w-full aspect-square object-cover bg-cream-200" />
            <div className="mt-2 flex items-center justify-between text-[10px]">
              <span className="text-ink-500">#{i + 1}</span>
              <div className="flex gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1 hover:bg-cream-200 disabled:opacity-30" aria-label="Move up" data-testid={`${testId}-up-${i}`}><ArrowUp size={12} /></button>
                <button type="button" onClick={() => move(i, +1)} disabled={i === value.length - 1} className="p-1 hover:bg-cream-200 disabled:opacity-30" aria-label="Move down" data-testid={`${testId}-down-${i}`}><ArrowDown size={12} /></button>
                <button type="button" onClick={() => removeAt(i)} className="p-1 hover:bg-destructive hover:text-white" aria-label="Remove" data-testid={`${testId}-remove-${i}`}><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
